import { ParsedPaywall } from "app/paywalls/mutations/createPaywall"
import { createContext, ReactNode, RefObject, useContext, useMemo, useRef } from "react"
import { ElementProvider } from "./ElementProvider"
import { TemplateJsonProvider } from "./JsonProvider"
import { LayoutProvider } from "./LayoutProvider"

type TMode = "create" | "edit"

type TPaywallBuilder = {
  children?: ReactNode
  iframeRef1?: RefObject<HTMLIFrameElement>
  iframeRef2?: RefObject<HTMLIFrameElement>
  mode: TMode
  onClosePaywallBuilder?: (url?: string) => void
  paywall?: ParsedPaywall
}

export const PaywallBuilderContext = createContext<TPaywallBuilder>({
  iframeRef1: undefined,
  iframeRef2: undefined,
  mode: "create",
  onClosePaywallBuilder: () => {},
})

type TProps = {
  children: ReactNode
  mode?: TMode
  onClosePaywallBuilder?: (url?: string) => void
  paywall?: ParsedPaywall
}

export const PaywallBuilderProvider = ({
  children,
  mode = "create",
  onClosePaywallBuilder = () => {},
  paywall,
}: TProps) => {
  const iframeRef1 = useRef<HTMLIFrameElement>(null)
  const iframeRef2 = useRef<HTMLIFrameElement>(null)

  const value = useMemo(
    () => ({ iframeRef1, iframeRef2, mode, onClosePaywallBuilder, paywall }),
    [iframeRef1, iframeRef2, mode, onClosePaywallBuilder, paywall]
  )

  return (
    <PaywallBuilderContext.Provider value={value}>
      <TemplateJsonProvider>
        <ElementProvider>
          <LayoutProvider>{children}</LayoutProvider>
        </ElementProvider>
      </TemplateJsonProvider>
    </PaywallBuilderContext.Provider>
  )
}

export const usePaywallBuilderContext = () => useContext(PaywallBuilderContext)
